import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {
  OrdersPolicyConfig,
  getOrdersPolicyConfig,
  updateOrdersPolicyConfig,
} from '../services/ordersPolicyService';

const OrdersPolicySettings: React.FC = () => {
  const [config, setConfig] = React.useState<OrdersPolicyConfig | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const next = await getOrdersPolicyConfig();
      setConfig(next);
    } catch (e: any) {
      setError(e?.message || 'Erreur lors du chargement de la politique de commande.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const updateField = (key: keyof OrdersPolicyConfig, value: boolean) => {
    setConfig((prev) => (prev ? { ...prev, [key]: value } : prev));
    setSuccess(null);
  };

  const handleSave = async () => {
    if (!config) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateOrdersPolicyConfig({
        allowClientOrderId: !!config.allowClientOrderId,
        enforceServerOrderId: !!config.enforceServerOrderId,
        writeClientServerMapping: !!config.writeClientServerMapping,
        enableStatusSanitization: !!config.enableStatusSanitization,
      });
      setConfig(updated);
      setSuccess('Politique de commande enregistrée.');
    } catch (e: any) {
      setError(e?.message || 'Erreur lors de l’enregistrement de la politique de commande.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Politique commandes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ces réglages définissent comment les commandes sont créées et validées côté serveur.
          </Typography>
        </Box>

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography>Chargement…</Typography>
          </Stack>
        ) : (
          <>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            <Alert severity="info">
              Modifie ces options seulement si tu veux changer le comportement général de création
              des commandes pour tout le réseau.
            </Alert>

            <FormControlLabel
              control={
                <Switch
                  checked={config?.allowClientOrderId ?? true}
                  onChange={(_, checked) => updateField('allowClientOrderId', checked)}
                />
              }
              label="Autoriser orderId fourni par le client"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={config?.enforceServerOrderId ?? false}
                  onChange={(_, checked) => updateField('enforceServerOrderId', checked)}
                />
              }
              label="Forcer un orderId serveur"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={config?.writeClientServerMapping ?? true}
                  onChange={(_, checked) => updateField('writeClientServerMapping', checked)}
                />
              }
              label="Écrire le mapping client_order_ids"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={config?.enableStatusSanitization ?? true}
                  onChange={(_, checked) => updateField('enableStatusSanitization', checked)}
                />
              }
              label="Sanitiser le statut initial envoyé par le client"
            />

            <Box>
              <Button variant="contained" onClick={handleSave} disabled={saving || !config}>
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default OrdersPolicySettings;
